import {
  EventClickArea,
  EventClickAttributes,
  EventClickElement,
} from '@tuic/tracking';

export type TrackingProps = {
  /** Maps to data-track-click-area */
  area?: `${EventClickArea}` | (string & {});
  /** Maps to data-track-click-element */
  element?: `${EventClickElement}` | (string & {});
  /** Maps to data-track-click-category */
  category?: string;
  /** Maps to data-track-click-label */
  label?: string;
  /** Maps to data-track-module */
  module?: string;
  /** Maps to data-track-module-type */
  moduleType?: string;
  /** Maps to data-track-position */
  position?: string;
  /** Maps to data-track-dialog */
  dialog?: string;
  /** Maps to data-track-delay */
  delay?: string;
};

/**
 * Convert TrackingProps to actual data attributes.
 * Can be used in any component without needing component-specific mapping.
 *
 * Example:
 * ```tsx
 * <div {...createClickTrackingProps({ module: 'carousel', moduleType: 'slider' })}>
 *   Content
 * </div>
 * ```
 *
 * Results in:
 * ```tsx
 * <div data-track-module="carousel" data-track-module-type="slider">
 *   Content
 * </div>
 * ```
 */
export const createClickTrackingProps = (
  tracking?: TrackingProps,
): ClickTracking => {
  if (!tracking) return {};

  const result: ClickTracking = {};

  // Map simple property names to their corresponding data attributes
  const propertyMap: Record<keyof TrackingProps, string> = {
    area: EventClickAttributes.Area,
    element: EventClickAttributes.Element,
    category: EventClickAttributes.Category,
    label: EventClickAttributes.Label,
    module: EventClickAttributes.Module,
    moduleType: EventClickAttributes.ModuleType,
    position: EventClickAttributes.Position,
    dialog: EventClickAttributes.Dialog,
    delay: EventClickAttributes.Delay,
  };

  for (const [key, value] of Object.entries(tracking)) {
    if (value !== undefined) {
      const dataAttribute = propertyMap[key as keyof TrackingProps];
      if (dataAttribute) {
        (result as Record<string, string>)[dataAttribute] =
          typeof value === 'string' ? value : String(value);
      }
    }
  }

  return result;
};

/**
 * Properly typed data attributes for click tracking.
 * This is the output type of createClickTrackingProps() and what components should accept.
 *
 * Example:
 * ```tsx
 * const trackingProps: ClickTrackingProps = createClickTrackingProps({
 *   type: 'slide',
 *   element: EventClickElement.Icon
 * });
 *
 * <Component tracking={trackingProps} />
 * ```
 */
export type ClickTracking = {
  // => `${MyEnum}` | (string & {})
  // Allows us to use auto-completion for known enum values
  // while still allowing arbitrary strings

  [EventClickAttributes.Area]?: `${EventClickArea}` | (string & {});
  [EventClickAttributes.Element]?: `${EventClickElement}` | (string & {});
  [EventClickAttributes.Category]?: string;
  [EventClickAttributes.Label]?: string;
  [EventClickAttributes.Module]?: string;
  [EventClickAttributes.ModuleType]?: string;
  [EventClickAttributes.Position]?: string;
  [EventClickAttributes.Dialog]?: string;
  [EventClickAttributes.Delay]?: string;
};

export type ClickTrackingProps = {
  tracking?: ClickTracking;
};
